/**
 * MakedoConfig — Shared mutable configuration singleton.
 * 
 * Values are set once by the facade layer (vibelive-api.js) during init().
 * Internal modules (fetch.js, websocket.js, etc.) read from this at call time,
 * NOT at import time — so no import map is required.
 * 
 * Context path auto-detected from the SDK script URL:
 *   /<devctx>/sdk/foo.js  →  ctxPath = "<devctx>/"
 *   /sdk/foo.js           →  ctxPath = ""  (ROOT deploy)
 */

const DEFAULT_SERVER = 'https://makedo.com';

// --- Auto-detect context path from this script's own URL ---
const _scriptUrl = (typeof import.meta !== 'undefined' && import.meta.url)
    ? import.meta.url
    : (typeof document !== 'undefined' && document.currentScript && document.currentScript.src);

let _ctxPath = '';   // e.g. "<devctx>/"  or  ""
let _sdkBase = '';   // e.g. "http://localhost:8080/<devctx>/sdk/"
if (_scriptUrl) {
    const _parsed = new URL(_scriptUrl);
    const _path = _parsed.pathname;                    // e.g. "/<devctx>/sdk/vibelive-api.js"
    const _sdkIdx = _path.indexOf('/sdk/');
    if (_sdkIdx !== -1) {
        // Everything between leading "/" and "/sdk/" is the context path
        _ctxPath = _path.substring(1, _sdkIdx + 1);   // "<devctx>/" or ""
        _sdkBase = _parsed.origin + _path.substring(0, _sdkIdx) + '/sdk/';
    }
}

const MakedoConfig = {
    // Set by configure(). Consumers must read via getters below.
    _serverUrl: DEFAULT_SERVER,
    _apiUrlBase: null,
    _imageUrlBase: null,
    _websocketServerRoute: null,

    /** One-time setup. Called by vibelive-api.js init(). */
    configure(serverUrl) {
        if (serverUrl) {
            // Strip trailing slash
            this._serverUrl = serverUrl.replace(/\/+$/, '');
        }
        const base = this._serverUrl;
        const wsBase = base.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
        this._apiUrlBase = `${base}/${_ctxPath}`;
        this._imageUrlBase = `${base}/staging/`;
        this._websocketServerRoute = `${wsBase}/${_ctxPath}signaling4`;
    },

    // --- Lazy getters — safe to call before or after configure() ---

    get apiUrlBase() {
        if (!this._apiUrlBase) this.configure();
        return this._apiUrlBase;
    },
    get imageUrlBase() {
        if (!this._imageUrlBase) this.configure();
        return this._imageUrlBase;
    },
    get websocketServerRoute() {
        if (!this._websocketServerRoute) this.configure();
        return this._websocketServerRoute;
    },
    /** Context path segment, e.g. "<devctx>/" or "" */
    get ctxPath() { return _ctxPath; },
    /** Full SDK base URL, e.g. "http://localhost:8080/<devctx>/sdk/" */
    get sdkBase() { return _sdkBase; }
};

export default MakedoConfig;
